/**
 * NMS Product Video Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Handles video player controls with play/pause and mute/unmute functionality
 *
 * Features:
 * - Autoplay on first viewport entry (50% visible)
 * - Auto-pause when leaving viewport
 * - Muted autoplay fallback if browser blocks sound
 */

class NMSProductVideo extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];
  #intersectionObserver = null;
  #hasAutoplayedOnce = false;

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.videoId = element.id || `product-video-${Date.now()}`; // REQUIRED: Unique identifier

    // Store component data from element
    this.sectionId = this.videoId;

    // Autoplay settings - always enabled
    this.autoplayOnView = true;
    this.pauseOnLeave = true;

    // REQUIRED: Store in global registry for external access
    window.neemans = window.neemans || {};
    window.neemans.productVideo = window.neemans.productVideo || {};
    window.neemans.productVideo[this.videoId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init();
    this.#setupVideoRefs();
    this.#initializeVideoState();
    this.#bindVideoEvents();
    this.#setupViewportObserver();
    this.#isInitialized = true;
  }

  // REQUIRED: Scope all setup to this.element
  #setupVideoRefs() {
    // Query only within component scope
    this.refs.video = document.querySelector(`#video-${this.sectionId}`);
    this.refs.unmuteBtn = document.querySelector(`#unmute-btn-${this.sectionId}`);
    this.refs.muteBtn = document.querySelector(`#mute-btn-${this.sectionId}`);
    this.refs.playBtn = document.querySelector(`#play-btn-${this.sectionId}`);
    this.refs.pauseBtn = document.querySelector(`#pause-btn-${this.sectionId}`);
  }

  #initializeVideoState() {
    // Validate required elements
    if (!this.refs.video || !this.refs.unmuteBtn || !this.refs.muteBtn || !this.refs.playBtn || !this.refs.pauseBtn) {
      this.handleError(new Error('Required video elements not found'), 'Missing video controls');
      return;
    }

    // Set up global data in window.neemans namespace
    this.setGlobalData(`productVideoData.${this.videoId}`, {
      isPlaying: false,
      isMuted: true, // Start muted for autoplay
    });
  }

  #bindVideoEvents() {
    // Mute/Unmute functionality
    if (this.refs.unmuteBtn) {
      const unmuteHandler = () => this.#handleUnmute();
      this.refs.unmuteBtn.addEventListener('click', unmuteHandler);
      this.#eventListeners.push({ element: this.refs.unmuteBtn, event: 'click', handler: unmuteHandler });
    }

    if (this.refs.muteBtn) {
      const muteHandler = () => this.#handleMute();
      this.refs.muteBtn.addEventListener('click', muteHandler);
      this.#eventListeners.push({ element: this.refs.muteBtn, event: 'click', handler: muteHandler });
    }

    // Play/Pause functionality
    if (this.refs.playBtn) {
      const playHandler = () => this.#handlePlay();
      this.refs.playBtn.addEventListener('click', playHandler);
      this.#eventListeners.push({ element: this.refs.playBtn, event: 'click', handler: playHandler });
    }

    if (this.refs.pauseBtn) {
      const pauseHandler = () => this.#handlePause();
      this.refs.pauseBtn.addEventListener('click', pauseHandler);
      this.#eventListeners.push({ element: this.refs.pauseBtn, event: 'click', handler: pauseHandler });
    }

    // Video event listeners
    if (this.refs.video) {
      const endedHandler = () => this.#handleVideoEnded();
      const playHandler = () => this.#handleVideoPlay();
      const pauseHandler = () => this.#handleVideoPause();

      this.refs.video.addEventListener('ended', endedHandler);
      this.refs.video.addEventListener('play', playHandler);
      this.refs.video.addEventListener('pause', pauseHandler);

      this.#eventListeners.push({ element: this.refs.video, event: 'ended', handler: endedHandler });
      this.#eventListeners.push({ element: this.refs.video, event: 'play', handler: playHandler });
      this.#eventListeners.push({ element: this.refs.video, event: 'pause', handler: pauseHandler });
    }
  }

  #setupViewportObserver() {
    if (!this.refs.video || !this.autoplayOnView) return;

    // Create Intersection Observer for viewport detection
    const options = {
      root: null, // Use viewport as root
      rootMargin: '0px',
      threshold: 0.5, // Trigger when 50% of video is visible
    };

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          // Video entered viewport
          this.#handleViewportEnter();
        } else {
          // Video left viewport
          this.#handleViewportLeave();
        }
      }
    }, options);

    // Start observing the video element
    this.#intersectionObserver.observe(this.refs.video);
  }

  #handleViewportEnter() {
    if (!this.refs.video || !this.autoplayOnView) return;

    // Only autoplay on first time entering viewport
    if (this.#hasAutoplayedOnce) {
      return;
    }

    // Mark as autoplayed before attempting
    this.#hasAutoplayedOnce = true;

    // Mute video before autoplay
    this.refs.video.muted = true;
    this.setGlobalData(`productVideoData.${this.videoId}.isMuted`, true);
    this.#showUnmuteButton();

    // Autoplay video when it enters viewport (first time only)
    const playPromise = this.refs.video.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          // Video autoplayed successfully (muted)
        })
        .catch(() => {
          // Failed to autoplay even when muted
        });
    }
  }

  #handleViewportLeave() {
    if (!this.refs.video || !this.pauseOnLeave) return;

    // Pause video when it leaves viewport
    if (!this.refs.video.paused) {
      this.refs.video.pause();
    }
  }

  #handleUnmute() {
    try {
      this.refs.video.muted = false;
      this.setGlobalData(`productVideoData.${this.videoId}.isMuted`, false);
      this.#showMuteButton();
    } catch (error) {
      this.handleError(error, 'Failed to unmute video');
    }
  }

  #handleMute() {
    try {
      this.refs.video.muted = true;
      this.setGlobalData(`productVideoData.${this.videoId}.isMuted`, true);
      this.#showUnmuteButton();
    } catch (error) {
      this.handleError(error, 'Failed to mute video');
    }
  }

  #handlePlay() {
    try {
      this.refs.video.play();
      this.setGlobalData(`productVideoData.${this.videoId}.isPlaying`, true);
    } catch (error) {
      this.handleError(error, 'Failed to play video');
    }
  }

  #handlePause() {
    try {
      this.refs.video.pause();
      this.setGlobalData(`productVideoData.${this.videoId}.isPlaying`, false);
    } catch (error) {
      this.handleError(error, 'Failed to pause video');
    }
  }

  #handleVideoEnded() {
    this.setGlobalData(`productVideoData.${this.videoId}.isPlaying`, false);
    this.#showPlayButton();
  }

  #handleVideoPlay() {
    this.setGlobalData(`productVideoData.${this.videoId}.isPlaying`, true);
    this.#showPauseButton();
  }

  #handleVideoPause() {
    this.setGlobalData(`productVideoData.${this.videoId}.isPlaying`, false);
    this.#showPlayButton();
  }

  // Helper functions to show/hide buttons
  #showPlayButton() {
    this.refs.playBtn.style.display = 'flex';
    this.refs.pauseBtn.style.display = 'none';
  }

  #showPauseButton() {
    this.refs.playBtn.style.display = 'none';
    this.refs.pauseBtn.style.display = 'flex';
  }

  #showUnmuteButton() {
    this.refs.unmuteBtn.style.display = 'flex';
    this.refs.muteBtn.style.display = 'none';
  }

  #showMuteButton() {
    this.refs.unmuteBtn.style.display = 'none';
    this.refs.muteBtn.style.display = 'flex';
  }

  // Public API methods
  play() {
    this.#handlePlay();
  }

  pause() {
    this.#handlePause();
  }

  mute() {
    this.#handleMute();
  }

  unmute() {
    this.#handleUnmute();
  }

  isPlaying() {
    return this.getGlobalData(`productVideoData.${this.videoId}.isPlaying`) || false;
  }

  isMuted() {
    return this.getGlobalData(`productVideoData.${this.videoId}.isMuted`) || false;
  }

  destroy() {
    // Clean up event listeners
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];

    // Clean up intersection observer
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    // Clean up global reference
    if (window.neemans.productVideo?.[this.videoId]) {
      delete window.neemans.productVideo[this.videoId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSProductVideo() {
  if (!window.NMSBaseClass) {
    console.error('NMSProductVideo: NMSBaseClass required');
    return;
  }

  // Find all product video components
  const elements = document.querySelectorAll('.nms-product-video');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSProductVideo(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSProductVideo);
} else {
  initNMSProductVideo();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('.nms-product-video')) {
    initNMSProductVideo();
  }
});

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSProductVideo = NMSProductVideo;

// Global initialization function for other components to use
window.initNMSProductVideo = initNMSProductVideo;
